import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, CalendarIcon, Trash2, Receipt, DollarSign } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const MerchExpensesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState("month");
  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(endOfMonth(new Date()));

  const [expenseForm, setExpenseForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    description: "",
    amount: 0,
    category_id: "",
    payment_method_id: "",
    notes: ""
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [expensesRes, categoriesRes, pmRes] = await Promise.all([
        axios.get(`${API}/merch/expenses`, {
          params: {
            start_date: format(startDate, "yyyy-MM-dd"),
            end_date: format(endDate, "yyyy-MM-dd")
          },
          ...getAuthHeaders()
        }),
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/payment-methods`, getAuthHeaders())
      ]);
      setExpenses(expensesRes.data);
      setCategories(categoriesRes.data.filter(c => c.type === "expense"));
      setPaymentMethods(pmRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    const now = new Date();
    switch (value) {
      case "month":
        setStartDate(startOfMonth(now));
        setEndDate(endOfMonth(now));
        break;
      case "last_month":
        const lastMonth = subMonths(now, 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
        break;
      case "quarter":
        setStartDate(subMonths(startOfMonth(now), 2));
        setEndDate(endOfMonth(now));
        break;
      default:
        break;
    }
  };

  const handleCreateExpense = async (e) => {
    e.preventDefault();
    
    if (!expenseForm.category_id || !expenseForm.description || expenseForm.amount <= 0) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    try {
      await axios.post(`${API}/merch/expenses`, expenseForm, getAuthHeaders());
      toast.success("Gasto registrado");
      setDialogOpen(false);
      setExpenseForm({
        date: format(new Date(), "yyyy-MM-dd"),
        description: "",
        amount: 0,
        category_id: "",
        payment_method_id: "",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear gasto");
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    
    try {
      await axios.delete(`${API}/merch/expenses/${id}`, getAuthHeaders());
      toast.success("Gasto eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar gasto");
    }
  };

  // Group expenses by category for pie chart
  const expensesByCategory = expenses.reduce((acc, exp) => {
    const catName = exp.category_name || "Sin categoría";
    if (!acc[catName]) {
      acc[catName] = { name: catName, value: 0, color: exp.category_color || "#6B7280" };
    }
    acc[catName].value += exp.amount;
    return acc;
  }, {});

  const pieData = Object.values(expensesByCategory);
  const totalExpenses = expenses.reduce((sum, e) => sum + e.amount, 0);

  if (loading && expenses.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner w-8 h-8 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="merch-expenses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Gastos Merch</h1>
            <p className="text-[#6B7280]">Administra los gastos operativos de mercancía</p>
          </div>
          
          <div className="flex gap-2">
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-[180px] bg-white border-[#FECDD3]">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="month">Este Mes</SelectItem>
                <SelectItem value="last_month">Mes Anterior</SelectItem>
                <SelectItem value="quarter">Últimos 3 Meses</SelectItem>
              </SelectContent>
            </Select>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="new-expense-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo Gasto
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle className="text-[#4A4A4A]">Registrar Gasto de Merch</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateExpense} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha</Label>
                      <Input 
                        type="date"
                        value={expenseForm.date}
                        onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                        className="bg-white border-[#FECDD3]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Monto</Label>
                      <Input 
                        type="number"
                        value={expenseForm.amount || ""}
                        onChange={(e) => setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="bg-white border-[#FECDD3]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Input 
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                      placeholder="Describe el gasto..."
                      className="bg-white border-[#FECDD3]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categoría</Label>
                      <Select value={expenseForm.category_id} onValueChange={(v) => setExpenseForm({ ...expenseForm, category_id: v })}>
                        <SelectTrigger className="bg-white border-[#FECDD3]">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }}></div>
                                {cat.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Método de Pago</Label>
                      <Select value={expenseForm.payment_method_id} onValueChange={(v) => setExpenseForm({ ...expenseForm, payment_method_id: v })}>
                        <SelectTrigger className="bg-white border-[#FECDD3]">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {paymentMethods.map(pm => (
                            <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notas (opcional)</Label>
                    <Textarea 
                      value={expenseForm.notes}
                      onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                      placeholder="Notas adicionales..."
                      className="bg-white border-[#FECDD3]"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white">
                    Registrar Gasto
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#EF4444]/30 col-span-1">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#EF4444]/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#EF4444]" />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Total Gastos</p>
                  <p className="text-2xl font-bold text-[#EF4444]">{formatCurrency(totalExpenses)}</p>
                  <p className="text-xs text-[#6B7280]">{expenses.length} registros</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#4A4A4A] text-sm font-medium">Gastos por Categoría</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={25}
                          outerRadius={50}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    {pieData.map((item, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="text-[#6B7280] truncate">{item.name}</span>
                        <span className="text-[#4A4A4A] font-mono ml-auto">{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-[#6B7280] py-4">No hay gastos registrados</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Expenses Table */}
        <Card className="bg-white border-[#FECDD3]">
          <CardHeader>
            <CardTitle className="text-[#4A4A4A] font-['Manrope']">Listado de Gastos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Fecha</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Descripción</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Categoría</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[#6B7280]">Método</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Monto</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[#6B7280]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense.id} className="border-b border-[#E5E7EB] hover:bg-[#FEE2E2]/30">
                      <td className="py-3 px-4 text-[#6B7280]">{expense.date}</td>
                      <td className="py-3 px-4 text-[#4A4A4A]">{expense.description}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: expense.category_color }}></div>
                          <span className="text-[#6B7280]">{expense.category_name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-[#6B7280]">{expense.payment_method_name || "-"}</td>
                      <td className="py-3 px-4 text-right text-[#EF4444] font-mono">{formatCurrency(expense.amount)}</td>
                      <td className="py-3 px-4 text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteExpense(expense.id)}
                          className="text-[#EF4444] hover:bg-[#EF4444]/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {expenses.length === 0 && (
                <div className="text-center py-12 text-[#6B7280]">
                  <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No hay gastos en este período</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MerchExpensesPage;
